import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '@cloudscape-design/components/modal';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import Toggle from '@cloudscape-design/components/toggle';
import Alert from '@cloudscape-design/components/alert';
import Link from '@cloudscape-design/components/link';
import Spinner from '@cloudscape-design/components/spinner';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import { getFilters, getAIConfig, getLastSyncTime, getQueueStatus, AIConfig } from '../api/tickets';
import { FiltersSummary, AIConfigSummary } from './settings/ConfigSummaries';
import { triggerSyncWithCooldown, isSyncInProgress } from '../utils/sync';

interface Props {
  visible: boolean;
  onDismiss: () => void;
  onSyncStarted?: () => void;
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  try { return new Date(dateStr).toLocaleString(); } catch { return dateStr; }
}

export function SyncDataModal({ visible, onDismiss, onSyncStarted }: Props) {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<Record<string, unknown> | null>(null);
  const [aiConfig, setAiConfig] = useState<AIConfig | null>(null);
  const [lastSynced, setLastSynced] = useState('');
  const [queueDepth, setQueueDepth] = useState(0);
  const [loading, setLoading] = useState(true);
  const [fullSync, setFullSync] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [inProgress, setInProgress] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning'; text: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      const [filtersRes, aiRes, ts, qs] = await Promise.all([
        getFilters(), getAIConfig(), getLastSyncTime(), getQueueStatus(),
      ]);
      setFilters(filtersRes.filters);
      setAiConfig(aiRes.config);
      setLastSynced(ts || '');
      setQueueDepth(qs.queue.messagesAvailable + qs.queue.messagesInFlight);
    } catch (e: any) {
      setMessage({ type: 'error', text: `Failed to load current config: ${e.message}` });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (visible) load(); }, [visible, load]);

  useEffect(() => {
    if (!visible) return;
    const tick = () => setInProgress(isSyncInProgress());
    tick();
    const interval = setInterval(tick, 5000);
    return () => clearInterval(interval);
  }, [visible]);

  const handleSync = async () => {
    if (isSyncInProgress()) {
      setMessage({ type: 'warning', text: 'A sync is already in progress. Please wait a few minutes before triggering another.' });
      return;
    }
    setSyncing(true);
    setMessage(null);
    try {
      await triggerSyncWithCooldown(fullSync);
      setInProgress(true);
      setMessage({ type: 'success', text: 'Sync started. Tickets will update in a few minutes.' });
      onSyncStarted?.();
    } catch (e: any) {
      setMessage({ type: 'error', text: `Failed to trigger sync: ${e.message}` });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header="Sync data"
      size="large"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Toggle checked={fullSync} onChange={({ detail }) => setFullSync(detail.checked)}>Full sync</Toggle>
            <Button variant="link" onClick={onDismiss}>Close</Button>
            <Button variant="primary" iconName="refresh" onClick={handleSync} loading={syncing} disabled={inProgress && !syncing}>
              {inProgress ? 'Sync in progress…' : 'Sync now'}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {message && (
          <Alert type={message.type} dismissible onDismiss={() => setMessage(null)}>{message.text}</Alert>
        )}

        <StatusIndicator type="info">Last synced: {loading ? 'Loading…' : (lastSynced ? formatDate(lastSynced) : '—')}</StatusIndicator>
        {queueDepth > 0 && (
          <StatusIndicator type="in-progress">Queue: {queueDepth} ticket{queueDepth > 1 ? 's' : ''} pending summarization</StatusIndicator>
        )}

        <Box variant="p">
          This sync will fetch tickets from Tickety using the configuration below.{' '}
          <Link onFollow={(e) => { e.preventDefault(); onDismiss(); navigate('/settings'); }}>Change in Settings</Link>.
        </Box>

        {loading ? (
          <Spinner />
        ) : (
          <ColumnLayout columns={2} variant="text-grid">
            <div>
              <Box variant="h4" margin={{ bottom: 'xs' }}>Ticket filters</Box>
              <FiltersSummary filters={filters} />
            </div>
            <div>
              <Box variant="h4" margin={{ bottom: 'xs' }}>AI summarization</Box>
              <AIConfigSummary config={aiConfig} />
            </div>
          </ColumnLayout>
        )}
      </SpaceBetween>
    </Modal>
  );
}
